import _ from 'underscore';

import {
  ImageExperienceImage,
  getAutoCroppedImages,
  constants as C,
} from '@bingads-webui-campaign-react/image-experience';
import { addAsset } from '@bingads-webui-campaign-react/pmax-lite-data';

import { processImagePlacementsMetadata } from './image-asset-model-util';
import { TempImageBase64 } from '../media/default-image-base64';

const getImageInfoTasks = (imageModelList, imageOData) => {
  const uniqueIds = _.chain(imageModelList)
    .map(imageModel => imageModel.Asset.IdString || imageModel.Asset.Id)
    .compact()
    .uniq()
    .value();

  return _.map(uniqueIds, imageId => imageOData.getImageByIdAsync(imageId));
};

const getAllImagesInfoFromMT = (imageModelList, imageOData) => {
  const fullImagesInfo = [];

  return Promise.all(getImageInfoTasks(imageModelList, imageOData))
    .then((imagesInfo) => {
      fullImagesInfo.push(...imagesInfo);

      const existingIds = _.pluck(fullImagesInfo, 'Id');
      const baseAssetArrays = _.chain(imagesInfo)
        .pluck('BaseIdString')
        .compact()
        .difference(existingIds)
        .map(Id => ({ Asset: { Id } }))
        .value();

      return Promise.all(getImageInfoTasks(baseAssetArrays, imageOData));
    }).then((baseImagesInfo) => {
      fullImagesInfo.push(...baseImagesInfo);

      return _.reduce(fullImagesInfo, (memo, value) => _.extend(memo, {
        [value.IdString]: _.extend({
          baseId: value.BaseIdString,
          url: value.Url,
          originalUrl: value.OriginalUrl,
          metadata: value.Metadata,
          sourceType: value.SourceType,
          width: value.Width,
          height: value.Height,
          cropSettingOnOriginal: value.CropSettingOnOriginal,
          placementSettings: value.PlacementSettings,
          originalHeight: value.OriginalHeight,
          originalWidth: value.OriginalWidth,
        }),
      }), {});
    });
};

const getImageProps = (asset, imagesInfo) => {
  const idString = asset.IdString || String(asset.Id);
  const assetExtraInfo = imagesInfo[idString];

  return {
    id: idString,
    baseId: assetExtraInfo.baseId,
    metadata: assetExtraInfo.metadata,
    src: assetExtraInfo.url,
    type: C.subTypeToType[asset.SubType],
    associationType: asset.SubType,
    isLogo: false,
    width: assetExtraInfo.width,
    height: assetExtraInfo.height,
    cropSettings: {
      SourceX: _.get(asset, 'CropX', 0),
      SourceY: _.get(asset, 'CropY', 0),
      SourceWidth: _.get(asset, 'CropWidth', assetExtraInfo.width),
      SourceHeight: _.get(asset, 'CropHeight', assetExtraInfo.height),
    },
    isIncluded: true,
    isRecommendedImage: asset.IsRecommendedImage,
    disableEdit: asset.DisableEdit,
    originalUrl: assetExtraInfo.originalUrl,
    cropSettingOnOriginal: assetExtraInfo.cropSettingOnOriginal,
    placementSettings: assetExtraInfo.placementSettings,
    originalWidth: assetExtraInfo.originalWidth,
    originalHeight: assetExtraInfo.originalHeight,
  };
};

const buildImageObjArr = (imageModelList, imagesInfo, imageTypesWithOrder) =>
  _.chain(imageModelList)
    .map(imageModel => new ImageExperienceImage(getImageProps(imageModel.Asset, imagesInfo)))
    .sortBy(imageObj => imageTypesWithOrder.indexOf(imageObj.Type))
    .value();

export const getImagesFromMT = (imageModelList, imageOData) =>
  getAllImagesInfoFromMT(imageModelList, imageOData)
    .then((imagesInfo) => {
      const imageTypesWithOrder = C.getImageTypesWithOrder(true, true);

      let imageObjArr = buildImageObjArr(imageModelList, imagesInfo, imageTypesWithOrder);

      const mmaAspectRatios = C.getAspectRatios({
        isMultimediaAd: true,
        isMultimediaAdinMSANEnabled: true,
        isCombinedMsanAndMmaAspectRatios: true,
      });

      imageObjArr = _.filter(imageObjArr, imageObj => imageObj.type in mmaAspectRatios);

      const imageObjArrById = _.groupBy(imageObjArr, imageObj => imageObj.BaseId);

      const imageObjArrByIdWithMissingTypes = _.map(_.values(imageObjArrById), (imageArr) => {
        let baseImageId = _.first(imageArr).id;
        baseImageId = baseImageId.toString().indexOf('-') > -1 ? baseImageId.match(/(\S*)-/)[1] : baseImageId;
        // preprocess metadata in existing placement
        processImagePlacementsMetadata(imageArr, baseImageId, imagesInfo[baseImageId]);
        const missingTypes = _.difference(imageTypesWithOrder, _.pluck(imageArr, 'Type'));

        const autoCroppedImages = getAutoCroppedImages({
          originalImageToCrop: _.first(imageArr).BaseImage,
          isMultimediaAd: true,
          isCombinedMsanAndMmaAspectRatios: true,
        });

        return _.chain(imageTypesWithOrder)
          .map((type) => {
            const isMissing = _.contains(missingTypes, type);

            if (!isMissing) {
              return _.find(imageArr, image => image.type === type);
            }

            const placement = _.find(autoCroppedImages, image => image.type === type);

            if (placement) {
              placement.setIncluded(false);
              placement.setBaseImage(_.first(imageArr).BaseImage.clone());
              placement.setEffectSettings(placement.BaseImage.EffectSettings);
            }

            return placement;
          })
          .compact()
          .value();
      });

      return _.values(imageObjArrByIdWithMissingTypes);
    });

/* istanbul ignore next */
const getImageListFromImageInfos = (imageInfos) => {
  const imageTypesWithOrder = C.getImageTypesWithOrder(true, true);

  return _.map(imageInfos, (imageInfo) => {
    const autoCroppedImages = getAutoCroppedImages({
      originalImageToCrop: imageInfo,
      isMultimediaAd: true,
      isMultimediaAdinMSANEnabled: true,
      isCombinedMsanAndMmaAspectRatios: true,
    });

    return _.chain(imageTypesWithOrder)
      .map((type) => {
        const placement = _.find(autoCroppedImages, image => image.type === type);
        const [minWidth, minHeight] = C.getMinimumSizeForImage(type, true, C.imageOptionKeys.Image, true);

        if (placement && placement.width >= minWidth && placement.height >= minHeight) {
          placement.setIncluded(true);
        }

        return placement;
      })
      .compact()
      .value();
  });
};

const uploadSuggestedImageTasks = (suggestedImages, cid, aid) =>
  _.map(suggestedImages, (suggestedImage) => {
    const payload = {
      Asset: {
        '@odata.type': '#Model.Image',
        Type: 'GenericImage',
        SourceType: 'WebsiteImported',
        Url: suggestedImage.ImageUrl || suggestedImage.url,
      },
      FolderId: null,
    };

    return addAsset(payload, cid, aid);
  });

export const uploadSuggestedImages = (suggestedImages, imageOData, cid, aid) =>
  Promise.all(uploadSuggestedImageTasks(suggestedImages, cid, aid))
    .then((imageUploadResponses) => {
      const uniqueIds = _.chain(imageUploadResponses)
        .map(response => response.value)
        .unique()
        .value();

      return Promise.all(_.map(uniqueIds, imageId => imageOData.getImageByIdAsync(imageId)));
    })
    .then((imagesInfos) => {
      const imagesInfosWithSrc = _.map(imagesInfos, imageInfo => ({ ...imageInfo, Src: imageInfo.OriginalUrl }));

      return getImageListFromImageInfos(imagesInfosWithSrc);
    });

export const uploadTempImage = (imageOData, cid, aid) => {
  const payload = {
    Asset: {
      '@odata.type': '#Model.Image',
      SourceType: 'UserUploaded',
      Data: TempImageBase64,
      Type: 'GenericImage',
    },
    FolderId: null,
  };

  return addAsset(payload, cid, aid).then((response) => {
    const imageId = response.value;

    return imageOData.getImageByIdAsync(imageId);
  }).then((imageInfo) => {
    const imageInfos = [imageInfo];
    return getImageListFromImageInfos(imageInfos);
  });
};
